import React, { ReactElement } from 'react';
import { Translator } from '../redux/modules/localization/types';
import { ReactNode } from 'react';
import { Button } from '@rmwc/button';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@rmwc/dialog';
import ReactDOM from 'react-dom';
import ThemeComponent from '../ThemeComponent';
import classNames from 'classnames';
import { CircularProgress } from '@rmwc/circular-progress';

export interface MessageDialogProps {
    /** When displaying a message popup over a jQuery modal dialog (such as the call dialog), it must be passed as the parent element. */
    parent?: HTMLElement;
    title?: ReactNode;
    icon?: string;
    children?: ReactNode;
    wide?: boolean;
    error?: boolean;
    translate: Translator;
    showOK?: boolean;
    okButtonText?: string;
    classes?: string[];
}

export const showMessageDialog = (props: MessageDialogProps): Promise<void> => {
    return showDialog(
        (positiveAction, negativeAction) => (
            <BaseDialog
                open={true}
                {...props}
                showCancel={!(props.showOK ?? true)}
                onCancel={negativeAction}
                wide={props.wide ?? false}
                buttons={
                    (props.showOK ?? true)
                        ? [
                              <Button key="okButton" raised className="save" onClick={positiveAction}>
                                  {props.okButtonText ?? props.translate('button_ok')}
                              </Button>,
                          ]
                        : undefined
                }
            />
        ),
        props.parent
    );
};

export interface ConfirmationDialogProps {
    /** When displaying a message popup over a jQuery modal dialog (such as the call dialog), it must be passed as the parent element. */
    parent?: HTMLElement;
    title?: ReactNode;
    icon?: string;
    children?: ReactNode;
    wide?: boolean;
    error?: boolean;
    translate: Translator;
    okButtonText?: string;
    cancelButtonText?: string;
    otherNegativeButtonText?: string;
    warning?: boolean;
}

export const showConfirmationDialog = (props: ConfirmationDialogProps): Promise<void> => {
    return showDialog((positiveAction, negativeAction) => {
        const buttons = [
            <Button key="cancelButton" onClick={() => negativeAction()}>
                {props.cancelButtonText ?? props.translate('generic.cancel.button')}
            </Button>,
            ...(props.otherNegativeButtonText
                ? [
                      <Button key="otherNegativeButtonText" onClick={() => negativeAction(props.otherNegativeButtonText)}>
                          {props.otherNegativeButtonText}
                      </Button>,
                  ]
                : []),
            <Button key="okButton" raised onClick={positiveAction} className={props.warning ? 'warning' : 'save'}>
                {props.okButtonText ?? props.translate('button_ok')}
            </Button>,
        ];

        return <BaseDialog open={true} {...props} onCancel={negativeAction} wide={props.wide ?? false} buttons={buttons} />;
    }, props.parent);
};

export const isDialogVisible = () => document.querySelector('.dialog-placeholder') !== null;

export const showDialog = (
    render: (positiveAction: () => void, negativeAction: (reason?: string) => void) => ReactElement,
    parent?: HTMLElement
): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
        if (!isDialogVisible()) {
            const parentE = parent ?? document.body;
            const root = document.createElement('div');
            root.classList.add('dialog-placeholder');
            parentE.append(root);
            const clearDOM = (action: () => void) =>
                setTimeout(() => {
                    ReactDOM.unmountComponentAtNode(root);
                    parentE.removeChild(root);
                    action();
                });
            ReactDOM.render(
                render(
                    () => {
                        // Clear component from DOM trees after onclick is executed
                        clearDOM(() => resolve());
                    },
                    (reason = 'User cancelled') => {
                        clearDOM(() => reject(reason));
                    }
                ),
                root
            );
        } else {
            reject('Dialog already visible');
        }
    });
};

export interface BaseDialogProps {
    open: boolean;
    loading?: boolean;
    title?: ReactNode;
    icon?: String;
    children?: ReactNode;
    buttons?: ReactNode[];
    onCancel?: () => void;
    wide?: boolean;
    error?: boolean;
    translate: Translator;
    showCancel?: boolean;
    classes?: string[];
    turnOffDefaultStyles?: boolean;
}

export const BaseDialog = (props: BaseDialogProps) => {
    const { translate, classes, loading, turnOffDefaultStyles = false } = props;
    const showCancel = props.showCancel ?? true;
    const providedButtonCount = props.buttons?.length ?? 0;
    return (
        <ThemeComponent>
            <Dialog
                open={props.open}
                className={classNames(
                    {
                        'leaddesk-base-dialog': !turnOffDefaultStyles,
                        'leaddesk-base-dialog-wide': props.wide,
                    },
                    classes
                )}
                onClose={event => {
                    if (event.detail.action === 'close') {
                        props.onCancel?.();
                    }
                }}
            >
                {loading && (
                    <div className="leaddesk-spinner">
                        <div>
                            <CircularProgress size="medium" />
                        </div>
                    </div>
                )}
                {providedButtonCount === 0 && showCancel && (
                    <Button className="close-button" onClick={props.onCancel}>
                        <i className="material-icons">close</i>
                    </Button>
                )}
                {props.title && (
                    <DialogTitle>
                        {props.icon && <i className={'material-icons' + (props.error ? ' error' : '')}>{props.icon}</i>}
                        {props.title}
                    </DialogTitle>
                )}
                {!props.title && <div className="spacer"></div>}
                <DialogContent>{props.children}</DialogContent>
                {providedButtonCount > 0 && (
                    <DialogActions>
                        {providedButtonCount === 1 && showCancel && (
                            <Button type="button" onClick={props.onCancel}>
                                {translate('generic.cancel.button')}
                            </Button>
                        )}
                        {props.buttons}
                    </DialogActions>
                )}
                {providedButtonCount === 0 && <div className="spacer"></div>}
            </Dialog>
        </ThemeComponent>
    );
};
